import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { StaticImage } from "gatsby-plugin-image";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { BlockHeader } from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { useAppStoreIcons } from "@app/hooks/use-app-store-icons/useAppStoreIcons";
import { useViewVariant } from "@app/hooks/use-view-variant/useViewVariant";
import { IButton, IRichText, TextType } from "@app/models/storyblok/basic";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { useSiteData } from "@app/hooks";
import { LinkButtonAdapter } from "../link-button-adapter/LinkButtonAdapter";

type Props = {
  title: IRichText<TextType>;
  text: IRichText<TextType>;
  button: IButton[];
};

export const DownloadApp: FC<Props> = ({ title, text, button }) => {
  const siteData = useSiteData();
  const { appStore, playMarket } = useAppStoreIcons();
  const appStoreIcon = getImage(appStore);
  const playMarketIcon = getImage(playMarket);
  const { isContentOnly } = useViewVariant();

  return isContentOnly ? null : (
    <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
      <div
        className={classNames(
          "mt-20 px-5",
          "lg:mt-25 lg:px-16",
          "xl:mt-40 xl:px-24",
          "xxl:mx-auto xxl:max-w-container-wide xxl:px-20",
        )}
      >
        <div
          className={classNames(
            "border-divider text-primary-content flex flex-col items-center gap-10 overflow-hidden rounded-lg border",
            "md:grid md:grid-cols-2",
            "lg:min-h-[500px]",
            "xxl:mx-auto xxl:max-w-container",
            "xxxl:min-h-[815px]",
          )}
        >
          <div
            className={classNames(
              "flex flex-col justify-center px-7.5 pt-12 pb-10",
              "md:px-10 md:pb-12",
              "lg:h-full lg:px-12",
              "xxl:px-20",
            )}
          >
            <div
              className={classNames("mb-15", "xxl:max-w-[80%]", "xxxl:mb-30")}
            >
              <BlockHeader
                richText={title}
                className={classNames(
                  "text-large mb-1.5",
                  "lg:mb-2",
                  "xxxl:mb-5 xxxl:text-xx-large",
                )}
              />
              <div
                className={classNames("text-regular", "xxl:text-medium", {
                  "xxl:mb-10 mb-7 lg:mb-4": button?.length,
                })}
              >
                <BlockBody richText={text} />
              </div>
              {button?.length ? <LinkButtonAdapter data={button[0]} /> : null}
            </div>
            <div className={classNames("flex gap-x-4", "xxl:gap-x-6")}>
              <a
                href={siteData.iosLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GatsbyImage
                  image={appStoreIcon}
                  alt="Apple Store"
                  className={classNames("w-[93px]", "lg:w-25", "xxl:w-[153px]")}
                />
              </a>

              <a
                href={siteData.androidLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <GatsbyImage
                  image={playMarketIcon}
                  alt="Google Play"
                  className={classNames(
                    "w-[105px]",
                    "lg:w-[114px]",
                    "xxl:w-[173px]",
                  )}
                />
              </a>
            </div>
          </div>
          <div className={classNames("relative", "md:h-full md:w-full")}>
            <div
              className={classNames(
                "mx-10 overflow-visible",
                "sm:mx-15",
                "md:absolute md:right-0 md:bottom-0 md:mx-0 md:w-[370px] md:translate-x-[33%] md:translate-y-[5%]",
                "lg:w-[500px]",
                "xl:w-[540px] xl:translate-y-[10%]",
                "xxxl:w-[720px] xxxl:origin-bottom xxxl:translate-y-0 xxxl:scale-[110%]",
              )}
            >
              <StaticImage src={"./iphone.webp"} alt={"Iphone"} />
            </div>
          </div>
        </div>
      </div>
    </Fade>
  );
};
